import { useEffect, useState } from 'react';
import { textingApi, type MoveOptions } from '../api/client';
import { Button, Dialog, Notice, SelectField, TextAreaField } from './ui';

/**
 * Move to a team (1.13.0, Teampace 3073; Joey, Nov 1 2026): a conversation on your own number goes
 * to a ring group or queue. Before anything is sent the dialog shows both texts: yours (from your
 * number, which you can edit: "I'm passing your message to our Billing team… They'll text you from
 * (410) 555-0199.") and the team's (from its number: "Dana passed your message to us… answered in the
 * order they arrive"). The team sees the conversation and its history; texts to your number follow it
 * for 30 days.
 */
export function MoveToTeamDialog({ domain, conversationId, contact, onClose, onMoved }: { domain: string; conversationId: string; contact: string; onClose: () => void; onMoved: (to: string, team: string) => void }) {
  const [options, setOptions] = useState<MoveOptions | null>(null);
  const [error, setError] = useState('');
  const [team, setTeam] = useState('');
  const [text, setText] = useState('');
  const [edited, setEdited] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    textingApi
      .moveOptions(domain, conversationId)
      .then((o) => {
        if (!live) return;
        setOptions(o);
        const first = o.teams.find((t) => t.canText) ?? o.teams[0];
        if (first) setTeam(first.extension);
      })
      .catch((err) => live && setError(err instanceof Error ? err.message : String(err)));
    return () => {
      live = false;
    };
  }, [domain, conversationId]);

  const chosen = options?.teams.find((t) => t.extension === team) ?? null;
  // Your text follows the team chosen until you change it.
  useEffect(() => {
    if (chosen && !edited) setText(chosen.personText);
  }, [chosen, edited]);

  async function move() {
    if (!chosen) return;
    setBusy(true);
    setError('');
    try {
      const r = await textingApi.move(domain, conversationId, { team: chosen.extension, ...(note.trim() ? { note: note.trim() } : {}), text: options?.personCanText ? text : null });
      onMoved(r.conversation, r.moved.team);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t move.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Move to a team"
      subtitle={`${contact}: the team sees this conversation, and answers from its own number.`}
      foot={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon="send" disabled={busy || !chosen?.canText || options?.optedOut} onClick={() => void move()}>
            {busy ? 'Moving…' : options?.personCanText ? 'Move and send' : 'Move'}
          </Button>
        </>
      }
    >
      {!options && !error && <p className="muted">Loading teams…</p>}
      {error && <Notice tone="crit">{error}</Notice>}
      {options && (
        <div className="stack move-team">
          {options.optedOut && <Notice tone="warn">They opted out of your company’s texts, so they can’t be moved to a team until they text START.</Notice>}
          {options.teams.length === 0 ? (
            <Notice tone="warn">Your company has no ring group or queue that takes texts.</Notice>
          ) : (
            <SelectField
              label="Team"
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              options={options.teams.map((t) => ({ value: t.extension, label: `${t.name}${t.label ? ` · ${t.label}` : ''}${t.canText ? '' : ' (can’t text yet)'}` }))}
            />
          )}
          {chosen && !chosen.canText && chosen.reason && <Notice tone="warn">{chosen.reason}</Notice>}
          {chosen && (
            <>
              {options.personCanText ? (
                <TextAreaField
                  label="Your text, from your number"
                  rows={3}
                  value={text}
                  onChange={(e) => {
                    setText(e.target.value);
                    setEdited(true);
                  }}
                  hint="Sent when you press Move and send."
                />
              ) : (
                <Notice tone="neutral">Your number can’t text yet, so only the team’s text goes.</Notice>
              )}
              <div className="field">
                <span className="label-like">Then {chosen.name} texts, from {chosen.label ?? 'its number'}</span>
                <p className="tt-bubble">{chosen.teamText}</p>
                <small className="muted">While {chosen.name} is closed: “{chosen.closedText}” At night where they are, it waits until morning.</small>
              </div>
              <TextAreaField label="Note for the team (optional)" rows={2} value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder="What it’s about, what you’ve told them" />
              <small className="muted">Texts they send to your number go to {chosen.name} for the next {options.followDays} days.</small>
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}
